<template>
  <div class="auth-form">
    <div class="auth-header">
      <img src="/logo-mini.svg" alt="ForMind" class="auth-logo" />
      <h2>{{ t('auth.loginTitle') }}</h2>
    </div>

    <form @submit.prevent="handleLogin">
      <div class="form-group">
        <input type="text" v-model="username" :placeholder="t('common.username')" required :disabled="loading">
      </div>

      <div class="form-group">
        <input type="password" v-model="password" :placeholder="t('common.password')" required :disabled="loading">
      </div>

      <button type="submit" class="submit-btn" :disabled="loading">
        <span v-if="!loading">{{ t('common.login') }}</span>
        <span v-else class="btn-loading">
          <span class="spinner"></span>
          {{ t('auth.loggingIn') }}
        </span>
      </button>

      <Transition name="shake">
        <div v-if="error" class="error-message">
          {{ error }}
        </div>
      </Transition>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { getRecaptchaToken, hideRecaptchaBadge, loadRecaptcha, showRecaptchaBadge } from '@/utils/recaptcha'

const { proxy } = getCurrentInstance()!;
const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const emit = defineEmits(['success'])

const username = ref('')
const password = ref('')
const loading = ref(false)
const error = ref('')

onMounted(() => {
  showRecaptchaBadge()
  loadRecaptcha().catch(() => {})
})

router.beforeEach(() => {
  hideRecaptchaBadge()
})

async function handleLogin() {
  if (!username.value || !password.value) {
    error.value = t('auth.usernameRequired')
    return
  }

  loading.value = true
  error.value = ''

  try {
    const recaptchaToken = await getRecaptchaToken('login')
    const result = await authStore.login(username.value, password.value, recaptchaToken)

    if (result.success) {
      emit('success')
      await router.push('/chat');
    } else {
      error.value = result.error || t('auth.loginFailed')
    }
  } catch (err: any) {
    error.value = err.message || t('auth.loginError')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.auth-form {
  width: 100%;
}

.auth-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 2rem;
}

.auth-logo {
  width: 40px;
  height: 40px;
}

.auth-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.form-group {
  margin-bottom: 0.875rem;
}

.form-group input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--border-default);
  border-radius: 10px;
  font-size: 0.875rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-group input::placeholder {
  color: var(--text-muted);
}

.form-group input:focus {
  outline: none;
  border-color: var(--accent-primary);
  box-shadow: 0 0 0 3px rgba(74, 158, 255, 0.12);
}

.submit-btn {
  width: 100%;
  padding: 12px;
  background: var(--accent-primary);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.15s ease;
  margin-top: 0.75rem;
}

.submit-btn:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
  opacity: 0.8;
}

.submit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-loading {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  margin-top: 1rem;
  padding: 10px 12px;
  background: rgba(244, 67, 54, 0.08);
  color: var(--status-disconnected);
  border-radius: 8px;
  text-align: center;
  font-size: 0.8125rem;
  border: 1px solid rgba(244, 67, 54, 0.12);
}

.shake-enter-active {
  animation: shake 0.35s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  50% { transform: translateX(5px); }
  75% { transform: translateX(-3px); }
}
</style>
